import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, InputGroup, Button, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaMap, FaTags, FaImage, FaGlobe, FaStar } from 'react-icons/fa';
import { usePlaceContext } from '../PlaceContext';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { processImage } from '../../../utils/ImageProcessor';
import { validateTextInput } from '../../../components/common/TextValidator';
import '../../../styles/components/itinerary/custom_place/CreateCustomPlaceWidget.css';
import useApi from '../../../Api';
import Popup from '../../../components/common/popup/Popup';
import DOMPurify from 'dompurify';
import { useAuth } from '../../../AuthContext';
import { FaStar as RBFaStar } from 'react-icons/fa';

const InteractiveStarRating = ({ rating, onChange }) => {
  const [hover, setHover] = useState(0);
  const handleKeyDown = (e, value) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onChange(value);
    }
  };
  return (
    <div className="interactive-star-rating" role="radiogroup" aria-label="Star Rating">
      {[1, 2, 3, 4, 5].map(value => (
        <RBFaStar
          key={value}
          size={20}
          role="radio"
          aria-checked={value === rating}
          tabIndex={0}
          className={value <= (hover || rating || 0) ? "star selected" : "star"}
          onClick={() => onChange(value)}
          onKeyDown={(e) => handleKeyDown(e, value)}
          onMouseEnter={() => setHover(value)}
          onMouseLeave={() => setHover(0)}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </div>
  );
};

function CreateCustomPlaceWidget({ onClose, latitude, longitude, existingPlace, onUpdate }) {
  const { savePlace, hidePlaceInfoBox, showPlaceInfoBox } = usePlaceContext();
  const { user } = useAuth();
  const [name, setName] = useState(existingPlace ? existingPlace.name : '');
  const [location, setLocation] = useState(existingPlace ? existingPlace.location || '' : '');
  const [types, setTypes] = useState(existingPlace && existingPlace.types ? existingPlace.types.join(', ') : '');
  const [website, setWebsite] = useState(existingPlace ? existingPlace.website || '' : '');
  const [starRating, setStarRating] = useState(existingPlace ? (existingPlace.rating || null) : null);
  const [existingImagePreviews, setExistingImagePreviews] = useState(
    existingPlace
      ? (existingPlace.photos && existingPlace.photos.length > 0 ? existingPlace.photos.map(p => p.url) : (existingPlace.photoUrl ? [existingPlace.photoUrl] : []))
      : []
  );
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [typesError, setTypesError] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [starRatingError, setStarRatingError] = useState('');
  const [imageError, setImageError] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const saveButtonRef = useRef(null);
  const apiClient = useApi();
  const modalOpenRef = useRef(false);
  const initialDataRef = useRef({
    name: existingPlace ? existingPlace.name : '',
    location: existingPlace ? existingPlace.location || '' : '',
    types: existingPlace && existingPlace.types ? existingPlace.types.join(', ') : '',
    website: existingPlace ? existingPlace.website || '' : '',
    starRating: existingPlace ? (existingPlace.rating || null) : null,
    existingImagePreviews: existingPlace
      ? (existingPlace.photos && existingPlace.photos.length > 0 ? existingPlace.photos.map(p => p.url) : (existingPlace.photoUrl ? [existingPlace.photoUrl] : []))
      : []
  });

  useEffect(() => {
    if (modalOpenRef.current) {
      onClose();
      return;
    }
    modalOpenRef.current = true;
    return () => {
      modalOpenRef.current = false;
    };
  }, [onClose]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedContent()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [name, location, types, website, starRating, existingImagePreviews, newImagePreviews]);

  const hasUnsavedContent = useCallback(() => {
    const initial = initialDataRef.current;
    return (
      name.trim() !== initial.name ||
      location.trim() !== initial.location ||
      types.trim() !== initial.types ||
      website.trim() !== initial.website ||
      starRating !== initial.starRating ||
      newImagePreviews.length > 0 ||
      existingImagePreviews.join() !== initial.existingImagePreviews.join()
    );
  }, [name, location, types, website, starRating, existingImagePreviews, newImagePreviews]);

  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setName(value);
    const { errors } = validateTextInput(value, { field_name: "Name", max_length: 50, min_length: 1 });
    setNameError(errors.join(' '));
  }, []);

  const handleLocationChange = useCallback((e) => {
    const value = e.target.value;
    setLocation(value);
    if (value) {
      const { errors } = validateTextInput(value, { field_name: "Address", max_length: 60, min_length: 0 });
      setLocationError(errors.join(' '));
    } else {
      setLocationError('');
    }
  }, []);

  const handleTypesChange = useCallback((e) => {
    const value = e.target.value;
    setTypes(value);
    const tags = value.split(',').map(t => t.trim()).filter(t => t);
    let errorMsg = '';
    for (const tag of tags) {
      const { errors } = validateTextInput(tag, { field_name: "Type", max_length: 30, min_length: 1, allow_special_chars: true });
      if (errors.length) {
        errorMsg = errors.join(' ');
        break;
      }
    }
    setTypesError(errorMsg);
  }, []);

  const handleWebsiteChange = useCallback((e) => {
    const value = e.target.value;
    setWebsite(value);
    if (value.trim()) {
      try {
        const url = new URL(value);
        if (!['https:'].includes(url.protocol)) {
          setWebsiteError("The website URL must begin with 'https://'.");
        } else if (!url.hostname) {
          setWebsiteError("The website URL must include a valid hostname.");
        } else {
          setWebsiteError('');
        }
      } catch {
        setWebsiteError("The website URL is not formatted correctly. Ensure it starts with 'https://'.");
      }
    } else {
      setWebsiteError('');
    }
  }, []);

  const handleImageChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (existingImagePreviews.length + newImagePreviews.length + files.length > 10) {
      setImageError('You can upload a maximum of 10 images.');
      return;
    }
    setImageError('');
    setProcessingImages(true);
    const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg'];
    const processPromises = files.map(file => {
      if (!validImageTypes.includes(file.type)) {
        return Promise.resolve({ error: 'Please select valid image files (jpg, jpeg, png, gif).' });
      }
      return processImage(file, 2560, 1440, 1280, 720, 0.85, 5)
        .then(processedBlob => ({
          blob: processedBlob,
          previewUrl: URL.createObjectURL(processedBlob)
        }))
        .catch(() => ({ error: 'Error processing one of the images.' }));
    });
    const results = await Promise.all(processPromises);
    const newBlobs = [];
    const newPreviews = [];
    const errorsArr = [];
    results.forEach(res => {
      if (res.error) {
        errorsArr.push(res.error);
      } else {
        newBlobs.push(res.blob);
        newPreviews.push(res.previewUrl);
      }
    });
    if (errorsArr.length) {
      setImageError(errorsArr.join(' '));
    }
    setNewImages(prev => [...prev, ...newBlobs]);
    setNewImagePreviews(prev => [...prev, ...newPreviews]);
    setProcessingImages(false);
    e.target.value = null;
  }, [existingImagePreviews.length, newImagePreviews.length]);

  const handleRemoveImage = useCallback((index) => {
    const totalExisting = existingImagePreviews.length;
    if (index < totalExisting) {
      setExistingImagePreviews(prev => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - totalExisting;
      const removedPreview = newImagePreviews[newIndex];
      URL.revokeObjectURL(removedPreview);
      setNewImagePreviews(prev => prev.filter((_, i) => i !== newIndex));
      setNewImages(prev => prev.filter((_, i) => i !== newIndex));
    }
  }, [existingImagePreviews, newImagePreviews]);

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (nameError || locationError || typesError || websiteError || starRatingError) {
      setError('Please fix the errors above');
      return;
    }
    if (location.length > 60) {
      setError('Address must be 60 characters or less');
      return;
    }
    setError('');
    setLoading(true);
    const sanitizedName = DOMPurify.sanitize(name, { ALLOWED_TAGS: [] });
    const sanitizedLocation = DOMPurify.sanitize(location, { ALLOWED_TAGS: [] });
    const sanitizedTypes = DOMPurify.sanitize(types, { ALLOWED_TAGS: [] });
    const sanitizedWebsite = DOMPurify.sanitize(website, { ALLOWED_TAGS: [] });
    const finalRating = starRating && starRating > 0 ? starRating : null;
    const customPlaceData = {
      id: existingPlace ? (existingPlace.custom_place_id || existingPlace.id) : uuidv4(),
      name: sanitizedName,
      location: sanitizedLocation.trim() ? sanitizedLocation.trim() : null,
      latitude,
      longitude,
      types: sanitizedTypes.split(',').map(t => t.trim()).filter(t => t),
      website: sanitizedWebsite.trim() ? sanitizedWebsite.trim() : null,
      rating: finalRating,
      photos: existingImagePreviews.map(url => ({ url })),
      __detailsFetched: true,
      creator_id: existingPlace ? (existingPlace.creator_id || user.uid) : user.uid
    };
    const formData = new FormData();
    formData.append('custom_place_data', JSON.stringify(customPlaceData));
    newImages.forEach(blob => {
      formData.append('images', blob, `${customPlaceData.id}-${uuidv4()}.jpg`);
    });
    try {
      let response;
      if (existingPlace) {
        response = await apiClient.put('/places/custom', formData, {
          headers: {
            Authorization: `Bearer ${window.token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await apiClient.post('/places/custom', formData, {
          headers: {
            Authorization: `Bearer ${window.token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      const updatedPlace = {
        ...customPlaceData,
        place_id: response.data.place_id,
        custom_place_id: existingPlace ? (existingPlace.custom_place_id || customPlaceData.id) : customPlaceData.id,
        photos: response.data.photos || existingImagePreviews.map(url => ({ url }))
      };
      if (existingPlace) {
        if (onUpdate) onUpdate(updatedPlace);
        window.dispatchEvent(new CustomEvent('customPlaceUpdated', { detail: updatedPlace }));
      } else {
        savePlace(updatedPlace);
      }
      hidePlaceInfoBox();
      showPlaceInfoBox(updatedPlace);
      toast.success(existingPlace ? 'Custom Place Widget updated!' : 'Custom Place Widget created!', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      onClose();
    } catch (err) {
      const apiMessage = err.response && err.response.data && err.response.data.detail ? err.response.data.detail : (existingPlace ? 'Error updating custom place' : 'Error creating custom place');
      toast.error(apiMessage, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  }, [name, location, types, website, starRating, newImages, existingImagePreviews, newImagePreviews, nameError, locationError, typesError, websiteError, starRatingError, apiClient, existingPlace, latitude, longitude, onClose, savePlace, hidePlaceInfoBox, showPlaceInfoBox, onUpdate, user]);

  const handleAttemptClose = useCallback(() => {
    if (hasUnsavedContent()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedContent, onClose]);

  const handleSaveAndClose = useCallback(async () => {
    await handleSubmit();
    setShowConfirmClose(false);
  }, [handleSubmit]);

  const handleDiscardAndClose = useCallback(() => {
    setShowConfirmClose(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (showConfirmClose && saveButtonRef.current) {
      saveButtonRef.current.focus();
    }
  }, [showConfirmClose]);

  const isBusy = loading || processingImages;
  const totalImages = existingImagePreviews.length + newImagePreviews.length;
  const footerContent = (
    <div className="d-grid gap-2">
      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
        <Button className="btn-neutral" onClick={handleSubmit} disabled={isBusy} aria-label={existingPlace ? 'Update Place' : 'Confirm Place'}>
          {existingPlace ? 'Update' : 'Confirm'}
        </Button>
        {isBusy && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.6)' }}>
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Popup
        title={existingPlace ? 'Edit Place' : 'Add Place'}
        onCancel={handleAttemptClose}
        disableOverlayClose={false}
        className="popup-modal create-custom-place-modal"
        footer={footerContent}
        aria-modal="true"
        role="dialog"
      >
        <Form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
          <Form.Group className="mb-3" controlId="customName">
            <Form.Label className="sr-only">Name</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
              <Form.Control type="text" placeholder="Name" aria-label="Place Name" aria-required="true" value={name} onChange={handleNameChange} maxLength={50} required />
            </InputGroup>
            {nameError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{nameError}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="customLocation">
            <Form.Label className="sr-only">Address</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaMap /></InputGroup.Text>
              <Form.Control type="text" placeholder="Address (optional)" aria-label="Address" value={location} onChange={handleLocationChange} maxLength={60} />
            </InputGroup>
            {locationError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{locationError}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="customTypes">
            <Form.Label className="sr-only">Types</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaTags /></InputGroup.Text>
              <Form.Control type="text" placeholder="Types (e.g. hotel, restaurant)" aria-label="Types" value={types} onChange={handleTypesChange} maxLength={30} />
            </InputGroup>
            {typesError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{typesError}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="customWebsite">
            <Form.Label className="sr-only">Website</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaGlobe /></InputGroup.Text>
              <Form.Control type="text" placeholder="Website (optional)" aria-label="Website" value={website} onChange={handleWebsiteChange} maxLength={255} />
            </InputGroup>
            {websiteError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{websiteError}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="customStarRating">
            <Form.Label className="sr-only">Star Rating</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaStar /></InputGroup.Text>
              <InteractiveStarRating rating={starRating} onChange={(value) => {
                setStarRating(value);
                if (value < 0 || value > 5) {
                  setStarRatingError('Rating must be between 0 and 5.');
                } else {
                  setStarRatingError('');
                }
              }} />
            </InputGroup>
            {starRatingError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{starRatingError}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="customImages">
            <Form.Label className="sr-only">Images</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaImage /></InputGroup.Text>
              <Button
                className="btn-neutral upload-image-btn"
                type="button"
                disabled={isBusy || totalImages >= 10}
                aria-label="Upload Images"
                onClick={() => document.getElementById('customPlace-images').click()}
              >
                {(totalImages) > 0 ? 'Add More Images' : 'Upload Images'}
              </Button>
            </InputGroup>
            {imageError && <Form.Text className="text-danger" role="alert" aria-live="assertive">{imageError}</Form.Text>}
            <input type="file" id="customPlace-images" accept="image/png, image/jpeg, image/jpg, image/gif" style={{ display: 'none' }} multiple onChange={handleImageChange} />
          </Form.Group>
          {(totalImages) > 0 && (
            <div className="custom-place-image-preview-container d-flex flex-wrap gap-2 mt-2">
              {[...existingImagePreviews, ...newImagePreviews].map((preview, index) => (
                <div key={index} className="custom-place-image-preview">
                  <img src={preview} alt={`Custom Place ${index + 1}`} />
                  <button type="button" className="remove-image-button" aria-label={`Remove image ${index + 1}`} onClick={() => handleRemoveImage(index)}>×</button>
                </div>
              ))}
            </div>
          )}
          {error && <span className="text-danger" role="alert" aria-live="assertive">{error}</span>}
        </Form>
      </Popup>
      {showConfirmClose && (
        <Popup
          title="Unsaved Changes"
          onCancel={handleDiscardAndClose}
          disableOverlayClose={false}
          className="popup-modal logout-modal"
          footer={
            <>
              <Button
                variant="danger"
                onClick={handleSaveAndClose}
                aria-label="Save Changes"
                ref={saveButtonRef}
              >
                Save Changes
              </Button>
              <Button variant="secondary" onClick={handleDiscardAndClose} aria-label="Discard Changes">
                Discard Changes
              </Button>
            </>
          }
          aria-modal="true"
          role="dialog"
        >
          <p>You have unsaved changes. Would you like to save before closing?</p>
        </Popup>
      )}
    </>
  );
}

export default CreateCustomPlaceWidget;
